import React, { useCallback, useEffect, useMemo, useState } from 'react';
/**
 * MatchingRenderer — Connect left items to right items by tapping.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s68.
 */
import { QuestionRendererProps } from './types';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
import { motion, AnimatePresence } from 'framer-motion';
import { shuffleWithSeed } from '../../../utils/deterministicShuffle';
import { hexToRgba, FALLBACK_COLOR, type TailwindCSSProperties } from './rendererUtils';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
export function MatchingRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}: QuestionRendererProps) {
  const color = moduleColor || FALLBACK_COLOR;
  const config = question.data || {};
  // Normalize pairs from multiple data formats
  let pairs: Array<{
    left: string;
    right: string;
  }> = [];
  const rawPairs = config.pairs || config.matching?.pairs;
  if (Array.isArray(rawPairs) && rawPairs.length > 0) {
    pairs = rawPairs;
  } else if (Array.isArray(config.leftItems) && Array.isArray(config.rightItems) && config.correctMatches) {
    if (config.leftItems.length === config.rightItems.length) {
      const matches = config.correctMatches as Record<string, number>;
      pairs = config.leftItems.map((left: string, i: number) => ({
        left,
        right: config.rightItems[matches[String(i)] ?? i] ?? ''
      }));
    } else {
      console.warn('MatchingRenderer: leftItems and rightItems length mismatch');
    }
  }
  const leftItems = useMemo(() => pairs.map((p, i) => ({
    id: i,
    content: typeof p.left === 'string' ? p.left : String(p.left ?? '')
  })), [pairs]);
  const rightItems = useMemo(() => {
    const items = pairs.map((p, i) => ({
      id: i,
      content: typeof p.right === 'string' ? p.right : String(p.right ?? '')
    }));
    try {
      return shuffleWithSeed(items, question.id || 'fallback-seed').shuffled;
    } catch {
      return items;
    }
  }, [pairs, question.id]);
  const [selectedLeft, setSelectedLeft] = useState<number | null>(null);
  const [connections, setConnections] = useState<Record<number, number>>({});
  const [justConnected, setJustConnected] = useState<number | null>(null);
  useEffect(() => {
    setSelectedLeft(null);
    setConnections({});
    setJustConnected(null);
  }, [question.id]);
  useEffect(() => {
    onReadyChange?.(Object.keys(connections).length === leftItems.length);
  }, [connections, leftItems.length, onReadyChange]);
  // REN-1 R-03: migrated to useEdgeTriggeredSubmit; rAF wrap preserved to
  // decouple onAnswer from the render cycle (framer-motion layout bug).
  const answerPayload = useMemo(() => JSON.stringify(Object.entries(connections).map(([l, r]) => [Number(l), r])), [connections]);
  const handleEdgeSubmit = useCallback((payload: string) => {
    requestAnimationFrame(() => onAnswer(payload));
  }, [onAnswer]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, handleEdgeSubmit, question.id);
  // Guard: no pairs — rendered AFTER all hooks
  if (pairs.length === 0) {
    return <div className="text-center p-8 bg-amber-50 rounded-xl border-2 border-amber-200" data-id="element-11606">
        <p className="text-amber-700 font-bold" data-id="element-11607">
          ⚠️ Geen koppelparen beschikbaar
        </p>
        <p className="text-sm text-amber-600 mt-2" data-id="element-11608">
          Deze vraag heeft geen geldige koppeldata.
        </p>
      </div>;
  }
  const handleLeftClick = (id: number) => {
    if (connections[id] !== undefined) {
      const next = {
        ...connections
      };
      delete next[id];
      setConnections(next);
    }
    setSelectedLeft(id);
  };
  const handleRightClick = (id: number) => {
    if (selectedLeft === null) return;
    const existingLeft = Object.keys(connections).find(k => connections[Number(k)] === id);
    const next = {
      ...connections
    };
    if (existingLeft) delete next[Number(existingLeft)];
    next[selectedLeft] = id;
    setConnections(next);
    setJustConnected(selectedLeft);
    setTimeout(() => setJustConnected(null), 400);
    setSelectedLeft(null);
  };
  const handleLeftKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      document.getElementById(`left-item-${(index + 1) % leftItems.length}`)?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      document.getElementById(`left-item-${(index - 1 + leftItems.length) % leftItems.length}`)?.focus();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      document.getElementById('right-item-0')?.focus();
    }
  };
  const handleRightKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      document.getElementById(`right-item-${(index + 1) % rightItems.length}`)?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      document.getElementById(`right-item-${(index - 1 + rightItems.length) % rightItems.length}`)?.focus();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      document.getElementById('left-item-0')?.focus();
    }
  };
  const getPairNumber = (leftId: number): number | null => {
    const sorted = Object.keys(connections).map(Number).sort((a, b) => a - b);
    const idx = sorted.indexOf(leftId);
    return idx !== -1 ? idx + 1 : null;
  };
  const getRightPairInfo = (rightId: number) => {
    const leftId = Object.keys(connections).find(k => connections[Number(k)] === rightId);
    if (leftId === undefined) return null;
    const pairNum = getPairNumber(Number(leftId));
    return pairNum !== null ? {
      leftId: Number(leftId),
      pairNum
    } : null;
  };
  const matchedCount = Object.keys(connections).length;
  return <div className="w-full max-w-2xl mx-auto" data-id="element-11609">
      <div className="flex items-center justify-between mb-5 px-1" data-id="element-11610">
        <p className="text-slate-400 text-xs font-medium" data-id="element-11611">
          Kies links, dan rechts
        </p>
        <div className="flex items-center gap-2" data-id="element-11612">
          <div className="flex gap-1.5" data-id="element-11613">
            {leftItems.map(item => <motion.div key={item.id} initial={false} animate={{
            scale: connections[item.id] !== undefined ? 1 : 0.75
          }} className={`w-2 h-2 rounded-full transition-colors duration-300 ${connections[item.id] !== undefined ? '' : 'bg-slate-200'}`} style={connections[item.id] !== undefined ? {
            backgroundColor: hexToRgba(color, 0.6)
          } : undefined} data-id="element-11614" />)}
          </div>
          <span className="text-xs font-semibold text-slate-400 tabular-nums" data-id="element-11615">
            {matchedCount}/{leftItems.length}
          </span>
        </div>
      </div>

      <div className="flex gap-3 sm:gap-5" data-id="element-11616">
        {/* Left Column */}
        <div className="flex-1 space-y-2.5" role="listbox" aria-label="Linker items" data-id="element-11617">
          {leftItems.map((item, index) => {
          const isConnected = connections[item.id] !== undefined;
          const isSelected = selectedLeft === item.id;
          const pairNum = getPairNumber(item.id);
          const wasJust = justConnected === item.id;
          return <motion.button key={item.id} id={`left-item-${index}`} role="option" aria-selected={isSelected || isConnected} tabIndex={0} onClick={() => handleLeftClick(item.id)} onKeyDown={e => handleLeftKeyDown(e, index)} initial={{
            opacity: 0,
            x: -12
          }} animate={{
            opacity: 1,
            x: 0,
            scale: wasJust ? [1, 1.04, 1] : 1
          }} transition={{
            opacity: {
              delay: index * 0.05,
              duration: 0.3
            },
            x: {
              delay: index * 0.05,
              duration: 0.3
            },
            scale: {
              duration: 0.3
            }
          }} whileTap={{
            scale: 0.97
          }} className={`w-full px-3 py-2.5 sm:px-4 sm:py-3 rounded-xl border-2 text-left transition-colors relative focus:outline-none min-h-[44px] flex items-center ${isSelected ? 'z-10' : isConnected ? '' : 'border-slate-200 bg-white hover:border-slate-300 active:bg-slate-50'}`} style={isSelected ? {
            borderColor: color,
            backgroundColor: hexToRgba(color, 0.05),
            boxShadow: `0 4px 6px -1px ${hexToRgba(color, 0.1)}, 0 2px 4px -1px ${hexToRgba(color, 0.06)}`
          } : isConnected ? {
            borderColor: hexToRgba(color, 0.2),
            backgroundColor: hexToRgba(color, 0.08)
          } : undefined} data-id="element-11618">
                {isConnected && <motion.div initial={{
              scaleY: 0
            }} animate={{
              scaleY: 1
            }} className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full" style={{
              backgroundColor: hexToRgba(color, 0.6)
            }} data-id="element-11619" />}
                <div className={`text-sm sm:text-base font-medium flex-1 ${isConnected ? 'pl-1.5' : 'text-slate-700'}`} style={isConnected ? {
              color: color
            } : undefined} data-id="element-11620">
                  <LatexRenderer content={item.content} data-id="element-11621" />
                </div>
                <AnimatePresence data-id="element-11622">
                  {pairNum && <motion.span initial={{
                scale: 0,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} exit={{
                scale: 0,
                opacity: 0
              }} transition={{
                type: 'spring',
                stiffness: 500,
                damping: 25
              }} className="ml-2 flex-shrink-0 w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center" style={{
                backgroundColor: color
              }} data-id="element-11623">
                      {pairNum}
                    </motion.span>}
                </AnimatePresence>
                {isSelected && <motion.div className="absolute inset-0 rounded-xl border-2 pointer-events-none" style={{
              borderColor: color
            }} initial={false} animate={{
              boxShadow: [`0 0 0 0px ${hexToRgba(color, 0)}`, `0 0 0 4px ${hexToRgba(color, 0.2)}`, `0 0 0 0px ${hexToRgba(color, 0)}`]
            }} transition={{
              duration: 1.5,
              repeat: Infinity
            }} data-id="element-11624" />}
              </motion.button>;
        })}
        </div>

        {/* Right Column */}
        <div className="flex-1 space-y-2.5" role="listbox" aria-label="Rechter items" data-id="element-11625">
          {rightItems.map((item, index) => {
          const pairInfo = getRightPairInfo(item.id);
          const isConnected = pairInfo !== null;
          const isTargetable = selectedLeft !== null && !isConnected;
          const wasJust = justConnected !== null && pairInfo?.leftId === justConnected;
          return <motion.button key={item.id} id={`right-item-${index}`} role="option" aria-selected={isConnected} tabIndex={0} onClick={() => handleRightClick(item.id)} onKeyDown={e => handleRightKeyDown(e, index)} initial={{
            opacity: 0,
            x: 12
          }} animate={{
            opacity: 1,
            x: 0,
            scale: wasJust ? [1, 1.04, 1] : 1
          }} transition={{
            opacity: {
              delay: index * 0.05,
              duration: 0.3
            },
            x: {
              delay: index * 0.05,
              duration: 0.3
            },
            scale: {
              duration: 0.3
            }
          }} whileTap={{
            scale: 0.97
          }} className={`w-full px-3 py-2.5 sm:px-4 sm:py-3 rounded-xl border-2 text-left transition-colors relative focus:outline-none min-h-[44px] flex items-center ${isConnected ? '' : isTargetable ? 'border-slate-300 bg-slate-50 cursor-pointer shadow-inner' : 'border-slate-200 bg-white cursor-default opacity-80'}`} style={isConnected ? {
            borderColor: hexToRgba(color, 0.2),
            backgroundColor: hexToRgba(color, 0.08)
          } : isTargetable ? {
            '--tw-ring-color': hexToRgba(color, 0.3),
            '--tw-ring-offset-width': '0px',
            '--tw-ring-offset-color': '#fff',
            '--tw-ring-offset-shadow': 'var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color)',
            '--tw-ring-shadow': 'var(--tw-ring-inset) 0 0 0 calc(2px + var(--tw-ring-offset-width)) var(--tw-ring-color)'
          } as TailwindCSSProperties : undefined} onMouseEnter={e => {
            if (isTargetable) {
              e.currentTarget.style.borderColor = hexToRgba(color, 0.4);
              e.currentTarget.style.boxShadow = 'var(--tw-ring-offset-shadow), var(--tw-ring-shadow), 0 0 #0000';
            }
          }} onMouseLeave={e => {
            if (isTargetable) {
              e.currentTarget.style.borderColor = '';
              e.currentTarget.style.boxShadow = '';
            }
          }} data-id="element-11626">
                <AnimatePresence data-id="element-11627">
                  {pairInfo && <motion.span initial={{
                scale: 0,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} exit={{
                scale: 0,
                opacity: 0
              }} transition={{
                type: 'spring',
                stiffness: 500,
                damping: 25
              }} className="mr-2 flex-shrink-0 w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center" style={{
                backgroundColor: color
              }} data-id="element-11628">
                      {pairInfo.pairNum}
                    </motion.span>}
                </AnimatePresence>
                <div className={`text-sm sm:text-base font-medium flex-1 ${isConnected ? '' : 'text-slate-700'}`} style={isConnected ? {
              color: color
            } : undefined} data-id="element-11629">
                  <LatexRenderer content={item.content} data-id="element-11630" />
                </div>
                {isConnected && <motion.div initial={{
              scaleY: 0
            }} animate={{
              scaleY: 1
            }} className="absolute right-0 top-2 bottom-2 w-1 rounded-l-full" style={{
              backgroundColor: hexToRgba(color, 0.6)
            }} data-id="element-11631" />}
              </motion.button>;
        })}
        </div>
      </div>
    </div>;
}